import { useTranslation } from "react-i18next";
import { InfoTooltip } from "~/components/info-tooltip";
import { Text } from "~/components/text";
import { useSession } from "~/contexts/auth-context";
import { getDefaultCodesKind } from "~/shared/constants/codes-kind";
import {
  BacklistMetadataGapCard,
  BacklistMetadataGapCardProps,
} from "./backlist-metadata-gap-card";

type MetadataGapFilterName = BacklistMetadataGapCardProps["filterName"];

const thresholds: Record<MetadataGapFilterName, number> = {
  keywordCount: 5,
  descriptionWordCount: 300,
  themaCodeCount: 3,
  bisacCodeCount: 3,
};

type MetadataGapCardDefinition = Omit<
  BacklistMetadataGapCardProps,
  "threshold" | "subtitle"
> & { subtitle: (threshold: number) => string };

export const BacklistMetadataGaps = () => {
  const { t } = useTranslation(["home"]);
  const { organisation } = useSession();
  const usesThemaCodes = getDefaultCodesKind(organisation.settings) === "THEMA";

  const cards: MetadataGapCardDefinition[] = [
    {
      filterName: "keywordCount",
      subtitle: (threshold) =>
        t("home:metadataGaps.conditions.keywords", { count: threshold }),
      iconName: "square-stack",
      iconWrapperClassName: "bg-[#FDF0F6]",
      iconClassName: "text-[#F32C69]",
    },
    {
      filterName: "descriptionWordCount",
      subtitle: (threshold) =>
        t("home:metadataGaps.conditions.descriptionWords", {
          count: threshold,
        }),
      iconName: "text-align-start",
      iconWrapperClassName: "bg-primary-50",
      iconClassName: "text-primary-600",
    },
    {
      filterName: "themaCodeCount",
      subtitle: (threshold) =>
        t("home:metadataGaps.conditions.themaCodes", { count: threshold }),
      iconName: "tag",
      iconWrapperClassName: "bg-[#F3EEFD]",
      iconClassName: "text-[#7A3FE4]",
    },
    {
      filterName: "bisacCodeCount",
      subtitle: (threshold) =>
        t("home:metadataGaps.conditions.bisacCodes", { count: threshold }),
      iconName: "library",
      iconWrapperClassName: "bg-[#FEF1E6]",
      iconClassName: "text-[#E8701A]",
    },
  ];
  const visibleCards = cards
    .filter((card) => usesThemaCodes || card.filterName !== "themaCodeCount")
    .map(({ subtitle, ...card }) => {
      const threshold = thresholds[card.filterName];
      return { ...card, threshold, subtitle: subtitle(threshold) };
    });

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <Text as="h2" variant="heading">
          {t("home:metadataGaps.title")}
        </Text>
        <div className="flex items-center gap-1">
          <Text variant="muted">{t("home:metadataGaps.description")}</Text>
          <InfoTooltip
            label={t("home:metadataGaps.tooltipLabel")}
            text={t("home:metadataGaps.tooltip")}
            iconSize="small"
            side="right"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {visibleCards.map((card) => (
          <BacklistMetadataGapCard key={card.filterName} {...card} />
        ))}
      </div>
    </div>
  );
};
